import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, RefreshCw, Check, CircleDashed, Wrench, Save, Copy, Download, FileText, MessageCircle, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useLab } from "@/context/LabContext";
import { copyMarkdown, downloadText, exportPdf } from "@/lib/export";
import SolutionChat from "@/components/SolutionChat";
import FeedbackForm from "@/components/FeedbackForm";
import { ArguingDialog } from "@/components/ArguingDialog";

const RATING_SCALE = [1, 2, 3, 4, 5];
const norm = (s) => (s || "").trim().toLowerCase();

export default function SolutionsStep() {
  const { map, update, setStep, setMap } = useLab();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [chatSolution, setChatSolution] = useState(null);
  const [changeSummary, setChangeSummary] = useState(null);

  const rating = map.reflection?.change_rating || null;
  const rated = !!rating;
  const newNeeds = changeSummary?.newNeeds || null;

  const setRating = (n) => update({ reflection: { ...map.reflection, change_rating: n } });

  const applyChanges = (result, meta) => {
    const snapshot = {
      archived_at: new Date().toISOString(),
      position_a: map.position_a,
      position_b: map.position_b,
      needs_a: map.needs_a,
      needs_b: map.needs_b,
      solutions: map.solutions,
    };
    update({
      history: [...(map.history || []), snapshot],
      position_a: result.position_a || map.position_a,
      position_b: result.position_b || map.position_b,
      needs_a: result.needs_a || map.needs_a,
      needs_b: result.needs_b || map.needs_b,
      solutions: result.solutions || map.solutions,
    });
    const added = new Set([...(meta?.addedA || []), ...(meta?.addedB || [])].map(norm));
    setChangeSummary({ changes: meta?.changes || [], newNeeds: added });
    setChatSolution(null);
  };

  const regenerate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/ai/solutions", {
        issue: map.issue,
        position_a: map.position_a,
        position_b: map.position_b,
        needs_a: map.needs_a,
        needs_b: map.needs_b,
      });
      update({ solutions: data.solutions || [] });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setLoading(false);
  };

  const buildPayload = () => ({
    title: map.title || map.issue,
    issue: map.issue,
    position_a: map.position_a,
    position_b: map.position_b,
    concerns_a: map.concerns_a,
    needs_a: map.needs_a,
    needs_b: map.needs_b,
    solutions: map.solutions,
    reflection: map.reflection,
    history: map.history || [],
    last_step: 3,
  });

  const save = async () => {
    setSaving(true);
    try {
      const payload = buildPayload();
      let data;
      if (map.id) ({ data } = await api.put(`/maps/${map.id}`, payload));
      else ({ data } = await api.post("/maps", payload));
      setMap({ ...map, id: data.id });
      toast.success("Issue map saved.");
      return data.id;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const saveAndFinish = async () => {
    const id = await save();
    if (id) navigate("/dashboard");
  };

  const guardExport = (fn) => () => {
    if (!rated) {
      toast.error("Please answer the reflection question below before exporting.");
      return;
    }
    fn();
  };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
      <div className="flex items-start justify-between gap-4 mb-2">
        <h2 className="font-serif text-2xl sm:text-3xl tracking-tight text-[#2D2A26]">Collaborative Solutions</h2>
        <div className="flex flex-wrap items-center justify-end gap-2 shrink-0">
          <ArguingDialog />
          <button
            onClick={regenerate}
            disabled={loading}
            data-testid="regenerate-solutions-button"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm border border-[#4A5D4E]/30 text-[#4A5D4E] hover:bg-[#F5F2EA] transition-all disabled:opacity-60"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Regenerate
          </button>
        </div>
      </div>
      <p className="text-[#6E6860] mb-8">
        Novel, integrative ideas that try to satisfy needs on both sides — not just split the difference.
      </p>

      {changeSummary && (
        <div className="mb-8 rounded-2xl border border-[#4A5D4E]/30 bg-[#4A5D4E]/5 p-5" data-testid="change-summary-banner">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-[#2D2A26] mb-1">What changed after your discussion</p>
              {changeSummary.changes.length > 0 ? (
                <ul className="space-y-1">
                  {changeSummary.changes.map((c, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-[#2D2A26]" data-testid={`change-summary-item-${i}`}>
                      <Check className="h-4 w-4 mt-0.5 shrink-0 text-[#4A5D4E]" strokeWidth={1.75} />
                      <span>{c}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-[#6E6860]">Solutions were regenerated from your current needs.</p>
              )}
              {newNeeds && newNeeds.size > 0 && (
                <p className="text-xs text-[#6E6860] mt-2">Newly added needs appear <span className="font-bold text-[#2D2A26]">in bold</span> in the cards below.</p>
              )}
            </div>
            <button onClick={() => setChangeSummary(null)} data-testid="change-summary-dismiss" className="p-1.5 rounded-full text-[#6E6860] hover:bg-white transition-colors shrink-0">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="space-y-5">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-40 rounded-2xl bg-white border border-[#E8E3D9] animate-breathe" style={{ animationDelay: `${i * 0.15}s` }} />
          ))}
        </div>
      ) : (
        <div className="space-y-5" data-testid="solutions-list">
          {(map.solutions || []).map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
              className="rounded-2xl bg-white border border-[#E8E3D9] p-6 sm:p-7 shadow-sm"
              data-testid={`solution-card-${i}`}
            >
              <div className="flex items-baseline gap-3 mb-2">
                <span className="font-serif text-2xl text-[#4A5D4E]">{i + 1}</span>
                <h3 className="font-serif text-xl text-[#2D2A26]">{s.title}</h3>
              </div>
              <p className="text-[#2D2A26] leading-relaxed mb-5">{s.description}</p>

              <div className="grid sm:grid-cols-2 gap-4">
                <NeedList icon={Check} color="#4A5D4E" label="Needs met — your side" items={s.needs_satisfied_a} highlight={newNeeds} />
                <NeedList icon={Check} color="#C4A265" label="Needs met — other side" items={s.needs_satisfied_b} highlight={newNeeds} />
              </div>

              {(s.needs_unaddressed?.length > 0 || s.improvements) && (
                <div className="grid sm:grid-cols-2 gap-4 mt-4 pt-4 border-t border-[#E8E3D9]">
                  <UnaddressedList items={s.needs_unaddressed} highlight={newNeeds} />
                  {s.improvements && (
                    <div>
                      <p className="text-xs uppercase tracking-[0.15em] mb-2 text-[#4A5D4E]">Possible improvements</p>
                      <div className="flex gap-2.5">
                        <Wrench className="h-4 w-4 text-[#4A5D4E] mt-0.5 shrink-0" strokeWidth={1.5} />
                        <p className="text-sm text-[#2D2A26] leading-relaxed">{s.improvements}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="mt-5 pt-4 border-t border-[#E8E3D9]">
                <p className="text-sm text-[#6E6860] leading-relaxed mb-2">
                  Do you think this solution can be improved? If so, there must be some need of yours
                  that is unmet, or a problem that is not yet solved.
                </p>
                <button
                  onClick={() => setChatSolution(s)}
                  data-testid={`discuss-solution-${i}`}
                  className="inline-flex items-center gap-2 text-sm font-medium text-[#4A5D4E] hover:text-[#3B4A3E] underline underline-offset-4"
                >
                  <MessageCircle className="h-4 w-4" strokeWidth={1.75} /> Press here to identify your unmet needs
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Reflection + Save/Export */}
      <div className="mt-10 rounded-2xl bg-[#F5F2EA] border border-[#E8E3D9] p-6 sm:p-8" data-testid="reflect-export-box">
        <p className="text-sm uppercase tracking-[0.2em] text-[#6E6860] mb-5">Reflect, save &amp; export</p>

        <label className="block text-base font-medium text-[#2D2A26] mb-4">
          To what extent did this process change how you understand the conflict?
        </label>
        <div className="flex items-center gap-2 sm:gap-3" role="radiogroup" data-testid="change-rating-scale">
          {RATING_SCALE.map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => setRating(n)}
              data-testid={`change-rating-${n}`}
              aria-pressed={rating === n}
              className={`h-11 w-11 sm:h-12 sm:w-12 rounded-full border text-base font-medium transition-all ${
                rating === n
                  ? "bg-[#4A5D4E] text-[#FDFBF7] border-[#4A5D4E] scale-105 shadow-sm"
                  : "bg-white text-[#2D2A26] border-[#E8E3D9] hover:border-[#4A5D4E]/50 hover:-translate-y-0.5"
              }`}
            >
              {n}
            </button>
          ))}
        </div>
        <div className="flex justify-between max-w-[19rem] mt-2 text-xs text-[#6E6860]">
          <span>Not at all</span>
          <span>Completely</span>
        </div>

        <div className="mt-7 pt-6 border-t border-[#E8E3D9]">
          <p className="text-sm text-[#6E6860] mb-4">
            <span className="font-medium text-[#2D2A26]">Save</span> keeps it under “My Maps”.
            <span className="font-medium text-[#2D2A26]"> Copy</span> puts the text on your clipboard.
            <span className="font-medium text-[#2D2A26]"> Download</span> saves a plain text file, and
            <span className="font-medium text-[#2D2A26]"> PDF</span> saves a formatted document.
            {!rated && <span className="block mt-1 text-[#B27A70]">Please answer the question above to enable exporting.</span>}
          </p>
          <div className="flex flex-wrap gap-3">
            <button onClick={save} disabled={saving} data-testid="save-map-button" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#4A5D4E] text-[#FDFBF7] font-medium hover:bg-[#3B4A3E] transition-all hover:-translate-y-0.5 disabled:opacity-60">
              <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save"}
            </button>
            <button onClick={guardExport(async () => { const ok = await copyMarkdown(map); ok ? toast.success("Copied to clipboard.") : toast.error("Couldn't copy — try Download instead."); })} disabled={!rated} data-testid="copy-map-button" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white border border-[#E8E3D9] text-[#2D2A26] hover:bg-white/60 transition-all disabled:opacity-50 disabled:cursor-not-allowed">
              <Copy className="h-4 w-4" /> Copy
            </button>
            <button onClick={guardExport(() => downloadText(map))} disabled={!rated} data-testid="download-map-button" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white border border-[#E8E3D9] text-[#2D2A26] hover:bg-white/60 transition-all disabled:opacity-50 disabled:cursor-not-allowed">
              <Download className="h-4 w-4" /> Download (text file)
            </button>
            <button onClick={guardExport(() => exportPdf(map))} disabled={!rated} data-testid="export-pdf-button" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white border border-[#E8E3D9] text-[#2D2A26] hover:bg-white/60 transition-all disabled:opacity-50 disabled:cursor-not-allowed">
              <FileText className="h-4 w-4" /> PDF
            </button>
          </div>
        </div>
      </div>

      <div className="mt-10 flex justify-between">
        <button onClick={() => setStep(2)} data-testid="solutions-back-button" className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-[#6E6860] hover:bg-[#F5F2EA] transition-colors">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <button onClick={saveAndFinish} disabled={saving} data-testid="finish-button" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#4A5D4E] text-[#FDFBF7] font-medium hover:bg-[#3B4A3E] transition-all hover:-translate-y-0.5 disabled:opacity-60">
          Save &amp; finish
        </button>
      </div>

      {/* Prominent end-of-flow feedback card */}
      <div className="mt-12 rounded-3xl border border-[#C4A265]/40 bg-gradient-to-b from-[#4A5D4E]/[0.07] to-[#C4A265]/[0.07] p-7 sm:p-9 text-center" data-testid="feedback-card">
        <p className="text-xs uppercase tracking-[0.2em] text-[#6E6860] mb-3">In active development</p>
        <h3 className="font-serif text-2xl sm:text-3xl text-[#2D2A26] mb-2">
          Please… <span className="italic">PLEASE!</span> We want your feedback.
        </h3>
        <p className="text-[#6E6860] max-w-xl mx-auto mb-7">
          You've just been through the whole journey — you're exactly the person whose input we need.
          Tell us what helped, what fell flat, and what you'd love to see. It genuinely shapes what we build next.
        </p>
        <div className="max-w-lg mx-auto text-left">
          <FeedbackForm />
        </div>
      </div>


      {chatSolution && (
        <SolutionChat
          solution={chatSolution}
          context={{
            issue: map.issue,
            position_a: map.position_a,
            position_b: map.position_b,
            needs_a: map.needs_a,
            needs_b: map.needs_b,
            solutions: map.solutions,
          }}
          onClose={() => setChatSolution(null)}
          onApplied={applyChanges}
          onSaveProgress={save}
        />
      )}
    </motion.div>
  );
}

function NeedList({ icon: Icon, color, label, items, highlight }) {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <p className="text-xs uppercase tracking-[0.15em] mb-2" style={{ color }}>{label}</p>
      <ul className="space-y-1.5">
        {items.map((n, i) => {
          const isNew = highlight?.has((n || "").trim().toLowerCase());
          return (
            <li key={i} className="flex items-start gap-2 text-sm text-[#2D2A26]">
              <Icon className="h-4 w-4 mt-0.5 shrink-0" style={{ color }} strokeWidth={1.75} />
              <span className={isNew ? "font-bold" : ""}>
                {n}
                {isNew && <span className="ml-1.5 align-middle text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-[#4A5D4E] text-[#FDFBF7]">New</span>}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
const SIDE_BADGE = {
  yours: { label: "Yours", color: "#4A5D4E" },
  other: { label: "Other side", color: "#C4A265" },
  both: { label: "Both", color: "#6E6860" },
};

function UnaddressedList({ items, highlight }) {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <p className="text-xs uppercase tracking-[0.15em] mb-2 text-[#6E6860]">Needs Not Yet Fully Addressed</p>
      <ul className="space-y-1.5">
        {items.map((item, i) => {
          const need = typeof item === "string" ? item : item?.need || "";
          const side = typeof item === "string" ? null : item?.side;
          const badge = side && SIDE_BADGE[side];
          const isNew = highlight?.has(need.trim().toLowerCase());
          return (
            <li key={i} className="flex items-start gap-2 text-sm text-[#2D2A26]">
              <CircleDashed className="h-4 w-4 mt-0.5 shrink-0 text-[#6E6860]" strokeWidth={1.75} />
              <span className="flex-1">
                <span className={isNew ? "font-bold" : ""}>{need}</span>
                {badge && (
                  <span
                    className="ml-2 inline-block align-middle text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border"
                    style={{ color: badge.color, borderColor: `${badge.color}55` }}
                  >
                    {badge.label}
                  </span>
                )}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
